<template>
  <div id="security-deposit-form">
    <b-row class="mb-2">
      <!-- PAYMENT DATE -->
      <b-col cols="12" sm="6">
        <b-row class="mb-2">
          <b-col cols="12">
            <h5>Data</h5>
          </b-col>
          <b-col cols="12">
            <b-form-datepicker v-model="newPayment.date" placeholder="Data depagament" :locale="locale"
              :start-weekday="1" :show-decade-nav="false" :date-format-options="{
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }" hide-header />
          </b-col>
        </b-row>
      </b-col>
      <!-- PAYMENT AMOUNT -->
      <b-col cols="12" sm="6">
        <b-row class="mb-2">
          <b-col cols="12">
            <h5>Import</h5>
          </b-col>
          <b-col cols="12">
            <b-input-group append="€" class="input-group-merge">
              <b-form-input v-model="newPayment.pvpAmount" type="number" debounce="750" number />
            </b-input-group>
          </b-col>
        </b-row>
      </b-col>
      <!-- PAYMENT METHOD -->
      <b-col cols="12">
        <b-row class="mb-2">
          <b-col cols="12">
            <h5>Mètode de pagament</h5>
          </b-col>
          <b-col cols="12">
            <v-select v-model="newPayment.method" placeholder="Selecciona un mètode" :options="paymentMethodOptions"
              :reduce="(option) => option.value" />
          </b-col>
        </b-row>
      </b-col>
      <!-- LOCALIZATOR -->
      <b-col cols="12">
        <b-row class="mb-2">
          <b-col cols="12">
            <h5>Localitzador</h5>
          </b-col>
          <b-col cols="12">
            <b-form-input v-model="newPayment.localizator" placeholder="Localitzador del pagament"
              :formatter="removeCommasFormatter" lazy-formatter />
          </b-col>
        </b-row>
      </b-col>
      <!-- COMMENT -->
      <b-col cols="12">
        <b-row class="mb-1">
          <b-col cols="12">
            <h5>Comentari</h5>
          </b-col>
          <b-col cols="12">
            <b-form-textarea v-model="newPayment.comment" placeholder="Comentari sobre el pagament" rows="2" />
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12">
        <!-- INCOMPLETE PAYMENT ALERT -->
        <b-alert variant="danger" :show="showInsufficientPaymentAlert" class="my-1">
          <div class="alert-body font-weight-normal">
            L'import és insuficient per cobrir el cost de la fiança
          </div>
        </b-alert>
        <!-- EXCESIVE PAYMENT ALERT -->
        <b-alert variant="primary" :show="showExcesivePaymentAlert" class="my-1">
          <div class="alert-body font-weight-normal">
            L'import pagat serà superior al cost de la fiança
          </div>
        </b-alert>
      </b-col>
    </b-row>

    <!-- BUTTONS -->
    <b-row class="mb-1 d-flex justify-content-between justify-content-sm-end">
      <b-col cols="6" sm="auto">
        <b-button block variant="light" @click="cancel()"> Cancel·la </b-button>
      </b-col>
      <b-col cols="6" sm="auto">
        <b-button block variant="primary" @click="save()"> Guarda </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BFormInput,
  BInputGroup,
  BFormTextarea,
  BAlert,
  BFormDatepicker,
} from "bootstrap-vue";
import { paymentMethodOptions } from "@/utils/select-options";
import vSelect from "vue-select";
import { formatDateObjectToDatabaseDateTime } from "@/utils/formatters";

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BFormInput,
    BInputGroup,
    BFormTextarea,
    BAlert,
    vSelect,
    BFormDatepicker,
  },
  props: {
    service: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      newPayment: {
        date: formatDateObjectToDatabaseDateTime(new Date()),
        pvpAmount: null,
        method: "CREDIT_CARD",
        localizator: null,
        comment: "Pagament fiança",
      },
    };
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    loadingBooking() {
      return this.$store.getters["booking/loading"];
    },
    booking() {
      return this.$store.getters["booking/booking"];
    },
    loadingPayments() {
      return this.$store.getters["booking/loadingClientPayments"];
    },
    payments() {
      return this.$store.getters["booking/clientPayments"];
    },
    refunds() {
      return this.$store.getters["booking/clientRefunds"];
    },
    securityDepositChargedPrice() {
      return this.$store.getters["booking/securityDepositChargedPrice"];
    },
    paymentMethodOptions() {
      return paymentMethodOptions;
    },
    transformedPvpAmount() {
      if (!this.newPayment.pvpAmount) return null;
      return Math.round(this.newPayment.pvpAmount * 100);
    },
    securityDepositAmount() {
      return this.service?.pvpPrice ? this.service.pvpPrice / 100 : 300;
    },
    securityDepositPayments() {
      if (!this.payments || this.payments.length === 0) return [];
      return this.payments.filter((cp) => cp.scope === "SECURITY_DEPOSIT");
    },
    securityDepositRefunds() {
      if (!this.refunds || this.refunds.length === 0) return [];
      return this.refunds.filter((cp) => cp.scope === "SECURITY_DEPOSIT");
    },
    isPartialPayment() {
      if (!this.transformedPvpAmount) return false;
      return this.transformedPvpAmount < this.service.pvpPrice;
    },
    showInsufficientPaymentAlert() {
      if (!this.transformedPvpAmount) return false;
      return (
        this.transformedPvpAmount + this.securityDepositChargedPrice <
        this.service.pvpPrice
      );
    },
    showExcesivePaymentAlert() {
      if (!this.transformedPvpAmount) return false;
      return (
        this.transformedPvpAmount + this.securityDepositChargedPrice >
        this.service.pvpPrice
      );
    },
  },
  created() {
    this.newPayment.pvpAmount = this.securityDepositAmount;
  },
  methods: {
    removeCommasFormatter(value) {
      return value ? value.replace(/,/g, '') : value;
    },
    cancel() {
      this.$emit("cancel");
    },
    save() {
      this.createPayment();
      // TODO: add edit payment method
    },
    createPayment() {
      this.$store.dispatch("app/setLoading", true);

      setTimeout(() => {
        this.$store
          .dispatch("booking/addClientPayment", {
            booking: this.booking["@id"],
            bookingService: this.service["@id"] || null,
            ...this.newPayment,
            pvpAmount: this.transformedPvpAmount,
            status: "CONFIRMED",
            confirmedAt: formatDateObjectToDatabaseDateTime(new Date()),
            scope: "SECURITY_DEPOSIT",
          })
          .then(() => {
            this.$emit("security-deposit-added");
            this.$store.dispatch("booking/fetchServices", this.booking.uuid);
          })
          .catch(() => {
            this.$emit("security-deposit-add-error");
            // TODO: Log error in Sentry? Only if is not logged in the store
          })
          .finally(() => this.$store.dispatch("app/setLoading", false));
      }, 100);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
